import { api } from '@/services/api/AxiosBaseService'

/** Estados do chamado. */
export type ConversationStatus = 'ABERTO' | 'PENDENTE' | 'ADIADO' | 'ENCERRADO'

/** Abas da fila. */
export type QueueScope = 'minhas' | 'nao_atribuidas' | 'equipe' | 'todas'

export interface ConversationCard {
  id: string
  status: ConversationStatus
  channel: 'APP' | 'WHATSAPP' | null
  inboxName: string | null
  contact: { id: string; name: string; phone: string | null; subscriberId: string | null }
  assignee: { id: string; name: string } | null
  team: { id: string; name: string } | null
  labels: Array<{ id: string; name: string; color: string }>
  lastMessage: string | null
  unread: number
  lastMessageAt: string | null
  /** Fim da janela de 24h do WhatsApp. Passou disso, só modelo aprovado. */
  windowExpiresAt: string | null
  formatted: { lastMessageAt: string | null; resolvedAt: string | null }
}

export interface TimelineMessage {
  kind: 'mensagem'
  id: string
  message: string
  isNote: boolean
  fromClient: boolean
  authorName: string | null
  status?: string | null
  failed?: string | null
  attachments: Array<{ id: string; kind: string; name: string | null; mime: string | null; size: number | null; url: string | null }>
  createdAt: string | null
  formatted: { createdAt: string | null }
}

export interface TimelineEvent {
  kind: 'evento'
  id: string
  type: string
  note: string | null
  from: string | null
  to: string | null
  authorName: string | null
  createdAt: string | null
  formatted: { createdAt: string | null }
}

export type TimelineItem = TimelineMessage | TimelineEvent

export interface ContextPanel {
  linked: boolean
  id?: string
  name?: string
  cpf?: string
  plan?: string | null
  status?: string
  card?: string | null
  invoices?: Array<{ id: string; code: string; status: string; amount: string; dueDate: string | null }>
  dependents?: Array<{ id: string; name: string }>
}

/** Resultado da busca de assinante para vincular ao contato. */
export interface SubscriberOption {
  id: string
  name: string
  cpf: string | null
  phone: string | null
  status: string
}

export interface ConversationThread {
  conversation: ConversationCard
  context: ContextPanel
  timeline: TimelineItem[]
  /** Chamados anteriores do mesmo contato — o histórico nunca se perde. */
  history: Array<{ id: string; status: ConversationStatus; formatted: { lastMessageAt: string | null; resolvedAt: string | null } }>
}

/**
 * Contadores das filas.
 *
 * As chaves são camelCase porque o middleware de resposta converte tudo — o
 * backend devolve `nao_atribuidas` e chega aqui como `naoAtribuidas`. O id do
 * filtro (QueueScope) continua snake_case porque é o que vai na requisição.
 */
export interface QueueCounters {
  minhas: number
  naoAtribuidas: number
  equipe: number
  todas: number
}

export interface WhatsAppTemplate {
  id: string
  name: string
  language: string
  category: string | null
  body: string | null
  variablesCount: number
}

export interface CannedResponse {
  id: string
  shortcut: string
  title: string
  content: string
}

interface ListParams {
  scope?: QueueScope
  status?: string
  inboxId?: string
  labelId?: string
  search?: string
  page?: number
}

/**
 * Central de Atendimento — fila, thread e ações do chamado.
 * Cada rota exige a sua permissão no backend (support.chat.*).
 */
export const SupportConversationsService = {
  async list(params: ListParams = {}): Promise<{ data: ConversationCard[]; counters: QueueCounters; lastPage: number }> {
    const r = await api.get<{ data: ConversationCard[]; meta: { counters: QueueCounters; lastPage: number } }>(
      '/support/conversations',
      {
        params: {
          scope: params.scope,
          status: params.status,
          inbox_id: params.inboxId,
          label_id: params.labelId,
          search: params.search || undefined,
          page: params.page,
        },
      },
    )
    return { data: r.data.data, counters: r.data.meta.counters, lastPage: r.data.meta.lastPage }
  },

  async thread(id: string): Promise<ConversationThread> {
    const r = await api.get<{ data: ConversationThread }>(`/support/conversations/${id}`)
    return r.data.data
  },

  /** Mensagem pública. Anexos vão como multipart. */
  async reply(id: string, message: string, files: File[] = []): Promise<TimelineMessage> {
    const r = await api.post<{ data: TimelineMessage }>(`/support/conversations/${id}/reply`, buildBody(message, files))
    return r.data.data
  },

  /**
   * Comentário interno. Rota separada da resposta de propósito: nota não pode
   * sair pelo mesmo caminho que fala com o cliente.
   */
  async note(id: string, message: string, files: File[] = []): Promise<TimelineMessage> {
    const r = await api.post<{ data: TimelineMessage }>(`/support/conversations/${id}/note`, buildBody(message, files))
    return r.data.data
  },

  async assign(id: string, userId?: string): Promise<ConversationCard> {
    const r = await api.post<{ data: ConversationCard }>(`/support/conversations/${id}/assign`, { user_id: userId })
    return r.data.data
  },

  async transfer(id: string, payload: { userId?: string; teamId?: string; reason: string }): Promise<ConversationCard> {
    const r = await api.post<{ data: ConversationCard }>(`/support/conversations/${id}/transfer`, {
      user_id: payload.userId, team_id: payload.teamId, reason: payload.reason,
    })
    return r.data.data
  },

  async resolve(id: string, note?: string): Promise<ConversationCard> {
    const r = await api.post<{ data: ConversationCard }>(`/support/conversations/${id}/resolve`, { note })
    return r.data.data
  },

  async reopen(id: string): Promise<ConversationCard> {
    const r = await api.post<{ data: ConversationCard }>(`/support/conversations/${id}/reopen`, {})
    return r.data.data
  },

  async snooze(id: string, until: string): Promise<ConversationCard> {
    const r = await api.post<{ data: ConversationCard }>(`/support/conversations/${id}/snooze`, { until })
    return r.data.data
  },

  async linkContact(id: string, subscriberId: string): Promise<ConversationCard> {
    const r = await api.post<{ data: ConversationCard }>(`/support/conversations/${id}/link-contact`, { subscriber_id: subscriberId })
    return r.data.data
  },

  /**
   * Devolve o chamado para a fila de não lidos. Abrir a conversa marca tudo
   * como lido, então quem quer voltar depois precisa desfazer.
   */
  async markUnread(id: string): Promise<ConversationCard> {
    const r = await api.post<{ data: ConversationCard }>(`/support/conversations/${id}/unread`, {})
    return r.data.data
  },

  /** Assinantes para vincular a um contato sem cadastro (nome, CPF ou telefone). */
  async subscriberSearch(q: string): Promise<SubscriberOption[]> {
    const r = await api.get<{ data: SubscriberOption[] }>('/support/subscriber-search', { params: { q } })
    return r.data.data
  },

  async syncLabels(id: string, labelIds: string[]): Promise<ConversationCard> {
    const r = await api.put<{ data: ConversationCard }>(`/support/conversations/${id}/labels`, { label_ids: labelIds })
    return r.data.data
  },

  /** Modelos aprovados — o único caminho quando a janela de 24h fecha. */
  async templates(): Promise<WhatsAppTemplate[]> {
    const r = await api.get<{ data: WhatsAppTemplate[] }>('/support/whatsapp-templates')
    return r.data.data
  },

  async sendTemplate(id: string, templateId: string, variables: string[]): Promise<TimelineMessage> {
    const r = await api.post<{ data: TimelineMessage }>(`/support/conversations/${id}/send-template`, {
      template_id: templateId, variables,
    })
    return r.data.data
  },

  async cannedResponses(conversationId?: string): Promise<CannedResponse[]> {
    const r = await api.get<{ data: CannedResponse[] }>('/support/canned-responses', {
      params: { conversation_id: conversationId },
    })
    return r.data.data
  },
}

/** Multipart quando há arquivo; JSON quando é só texto. */
function buildBody(message: string, files: File[]): FormData | { message: string } {
  if (files.length === 0) return { message }

  const fd = new FormData()
  fd.append('message', message)
  files.forEach((f) => fd.append('files[]', f))
  return fd
}
